import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import users from "../../data/users";
import env from '../../config';
import axios from 'axios';
import ImageModal from '../common/ImageModal';

// Variáveis de configuração fora do componente para evitar recriação
const ALL_BILLING_STATUSES = ['waiting', 'to_invoice', 'invoiced', 'no_invoice_needed'];
const STORAGE_KEY = 'billingFilterStatus'; // Chave para o localStorage

// Função para carregar o estado inicial do localStorage
const getInitialFilter = () => {
    const savedFilter = localStorage.getItem(STORAGE_KEY);
    // Se houver um filtro salvo, usa-o. Se não houver, usa todos os status como padrão.
    try {
        return savedFilter ? JSON.parse(savedFilter) : ALL_BILLING_STATUSES;
    } catch (e) {
        // Em caso de erro de parse, retorna o padrão
        console.error("Erro ao carregar filtro do localStorage:", e);
        return ALL_BILLING_STATUSES;
    }
};

const ExtraServiceList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const user = users.find((u) => u.id === parseInt(userId));
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  // 👈 ALTERAÇÃO 1: Carrega o estado inicial do localStorage usando a nova função
  const [billingFilter, setBillingFilter] = useState(getInitialFilter); 
  // Date range filter states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  let stylesLoaded = false;
  const backendUrl = env.backendUrl;
  const frontendUrl = env.frontendUrl;
  const url = backendUrl;
  const [selectedImage, setSelectedImage] = useState(null); // { projectId, index }
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setIsUploading(true); // começa o loading

    const isTailwindRoute = location.pathname.startsWith('/extraService-list');
    if (isTailwindRoute && !stylesLoaded) {
      import('../../tailwind.css')
        .then(() => {
          stylesLoaded = true;
          setIsUploading(false);
        })
        .catch(error => {
          console.error('Erro ao carregar estilos do Timesheet:', error);
        });
    }
  }, [location]);

  useEffect(() => {
    localStorage.setItem('userId', userId);

    if (userId === '0') {
      fetch(url + '/extra-services')
        .then((res) => res.json())
        .then(data => {
          const sortedData = data.data.sort((a, b) => {
            return new Date(b.request_date) - new Date(a.request_date);
          });
          setProjects(sortedData);
        })
        .catch((err) => console.error('Erro ao buscar projetos:', err));
    } else {
      fetch(url + '/extra-services/findByUserId/' + userId)
        .then((res) => res.json())
        .then(data => {
          // console.info(data);
          setProjects(data);
        })
        .catch((err) => console.error('Erro ao buscar projetos:', err));
    }


  }, []);

  // Initialize billingFilter from URL `billing` param if present (overrides localStorage)
  useEffect(() => {
    const billingParam = searchParams.get('billing');
    if (billingParam) {
      const parsed = billingParam.split(',').map(s => s.trim()).filter(Boolean);
      setBillingFilter(parsed);
    }
    const fromParam = searchParams.get('dateFrom');
    const toParam = searchParams.get('dateTo');
    if (fromParam) setDateFrom(fromParam);
    if (toParam) setDateTo(toParam);
  }, [searchParams]);

  // 👈 ALTERAÇÃO 2: Novo useEffect para SALVAR o filtro no localStorage sempre que ele mudar
 useEffect(() => {
    // save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(billingFilter));

    // sync to URL
    try {
      const params = new URLSearchParams(searchParams);
      // billing
      if (!billingFilter || billingFilter.length === 0) {
        params.delete('billing');
      } else {
        params.set('billing', billingFilter.join(','));
      }
      // dateFrom/dateTo
      if (dateFrom) params.set('dateFrom', dateFrom); else params.delete('dateFrom');
      if (dateTo) params.set('dateTo', dateTo); else params.delete('dateTo');

      setSearchParams(params, { replace: true });
    } catch (err) {
      console.error('Error syncing filters to URL:', err);
    }
  }, [billingFilter, dateFrom, dateTo]);


  // Lógica para o checkbox "All status" e para gerenciar múltiplos status
  const handleBillingFilterChange = (status) => {
    if (status === 'all') {
      // Se 'all' for marcado, seleciona todos. Se for desmarcado, limpa todos (ou mantém vazio).
      setBillingFilter(billingFilter.length === ALL_BILLING_STATUSES.length ? [] : ALL_BILLING_STATUSES);
    } else {
      setBillingFilter(prevFilter => 
        prevFilter.includes(status)
          ? prevFilter.filter(item => item !== status) // Remove o status
          : [...prevFilter, status] // Adiciona o status
      );
    }
  };

  // ALTERAÇÃO 3: Função para filtrar projetos baseada nos status de billing selecionados
  const filteredProjects = projects.filter(project => {
    // Se nenhum filtro estiver selecionado, não mostra nada
    if (billingFilter.length === 0) return false;

    // Verifica se o status do projeto está incluso no array de filtros
    const statusMatch = billingFilter.includes(project.billing_status);

    // Filtragem por data (se fornecida)
    let dateMatch = true;
    if (dateFrom) {
      const from = new Date(dateFrom);
      const reqDate = new Date(project.request_date);
      if (reqDate < from) dateMatch = false;
    }
    if (dateTo) {
      // consider end of day for inclusive filter
      const to = new Date(dateTo);
      to.setHours(23,59,59,999);
      const reqDate = new Date(project.request_date);
      if (reqDate > to) dateMatch = false;
    }

    return statusMatch && dateMatch;
  });

  // Prepare modal props when an image is selected in the list view
  let modalImages = [];
  let modalIndex = 0;
  if (selectedImage) {
    const selProj = projects.find(p => p.id === selectedImage.projectId);
    modalImages = selProj ? (JSON.parse(selProj.reference_photos || '[]')) : [];
    modalIndex = selectedImage.index ?? 0;
  }


  // ... (O restante das funções handleEditService, handleProjectClick, etc., permanecem as mesmas)
  const handleEditService = (projectId) => {
    navigate(`/extraService-edit?id=${projectId}`);
  };

  const handleProjectClick = (projectId) => {
    navigate(`/extraService-details?id=${projectId}`);
  };

  const handlePrintResults = () => {
    const rowsHtml = filteredProjects.map(p => `
      <tr>
        <td style="padding:8px;border:1px solid #ddd">${p.request_date || ''}</td>
        <td style="padding:8px;border:1px solid #ddd">${(p.service_address || '').replace(/</g,'&lt;')}</td>
        <td style="padding:8px;border:1px solid #ddd">${(p.service_description || '').replace(/</g,'&lt;')}</td>
      </tr>`).join('');

    const html = `
      <html>
        <head>
          <title>Extra Services</title>
          <style>table{border-collapse:collapse;width:100%;font-family:Arial,Helvetica,sans-serif}th,td{padding:8px;border:1px solid #ddd;text-align:left}th{background:#f4f4f4}</style>
        </head>
        <body>
          <h2>Extra Services</h2>
          <table>
            <thead>
              <tr><th>Date</th><th>Address</th><th>Description</th></tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>`;

    const w = window.open('', '_blank');
    if (!w) {
      alert('Unable to open print window (popups blocked).');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    // Slight delay to ensure resources render
    setTimeout(() => { w.print(); }, 300);
  };

  const handlePhotoUpload = (e, service_id) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map((file) => file);

    const formData = new FormData();
    formData.append('image', newPhotos[0]);

    setIsUploading(true); // começa o loading
    axios.post(url + `/api/upload-image-drive`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((response) => {
        const driveUrl = response.data.path;
        setIsUploading(false);

        // Atualizar o projeto correto
        setProjects(prevProjects => prevProjects.map(proj => {
          if (proj.id === service_id) {
            const updatedPhotos = [
              ...(proj.reference_photos ? JSON.parse(proj.reference_photos) : []),
              driveUrl
            ];
            // Atualiza o servidor
            axios.put(url + `/extra-services/${service_id}`, {
              reference_photos: updatedPhotos,
            });
            return { ...proj, reference_photos: JSON.stringify(updatedPhotos) };
          }
          return proj;
        }));
      })
      .catch((error) => {
        console.error(error);
        setIsUploading(false);
      });
  };

  const handleRemovePhoto = (index, service_id) => {
    const confirmDelete = window.confirm('Are you sure?');
    if (!confirmDelete) return;

    
    const photoToDelete = JSON.parse(projects[0].reference_photos)[index];
    console.log('Photo URL to delete:', photoToDelete);

    const fileId = photoToDelete.match(/id=([^&]+)/)?.[1];
    console.log('Extracted file ID:', fileId);

    if (!fileId) {
      console.error('Could not extract file ID from URL');
      return;
    }
    

    setProjects(prevProjects => prevProjects.map(proj => {
      if (proj.id === service_id) {
        const currentPhotos = proj.reference_photos ? JSON.parse(proj.reference_photos) : [];
        const updatedPhotos = currentPhotos.filter((_, i) => i !== index);

        axios.put(url + `/extra-services/${service_id}`, {
          reference_photos: updatedPhotos,
        })
        .then(response => {
        // Aqui você pode manipular a resposta da requisição
        console.log('Fotos atualizadas com sucesso!', response.data);
        deleteImage(fileId);
      })
      .catch(error => {
        // Aqui você pode tratar os erros
        console.error('Erro ao atualizar as fotos', error);
      });

        return { ...proj, reference_photos: JSON.stringify(updatedPhotos) };
      }
      return proj;
    }));
  };

  const deleteImage = (fileId) => {
    // First delete from Google Drive
    setIsUploading(true);
    console.log('Sending delete request with fileId:', fileId);

    axios.delete(url + '/api/images/google-drive', {
      data: { fileId: fileId }
    })
    .catch((error) => {
      console.error('Error removing photo:', error);

      alert('Error removing photo. Please try again.');
      setIsUploading(false);
    });

    setIsUploading(false);

  }

  const handleDeleteService = (serviceId) => {
    const confirmDelete = window.confirm('Are you sure?');
    if (!confirmDelete) return;

    axios.delete(`${url}/extra-services/${serviceId}`)
      .then(() => {
        setProjects(prevProjects => prevProjects.filter(project => project.id !== serviceId));
      })
      .catch(error => {
        console.error('Erro ao excluir serviço:', error);
        alert('Erro ao excluir serviço.');
      });
  };

  const handleCopyToClipboard = (service) => {
    // const photos = JSON.parse(service.reference_photos || '[]');
    // const imageUrl = photos.length ? `${url}/storage/${photos[0]}` : '';
    //   const message = `📍 *Address:* ${service.service_address}
    // 📦 *Status:* ${service.request_status.replace('_', ' ')}
    // 💰 *Billing:* ${service.billing_status.replace('_', ' ')}

    // ${imageUrl ? `🖼 Image: ${imageUrl}` : ''}`;

    // const whatsappLink = `https://wa.me/15551234567?text=${encodeURIComponent(message)}`;

    // Abre o link do WhatsApp em uma nova aba
    // window.open(whatsappLink, '_blank');

    sendWhatsappMessage(service);
  };

  const handleSendToCustomer = (service) => {
    const emails = service.supervisor_emails
      .split(/[,;]\s*/)   // quebra por vírgula ou ponto e vírgula, com ou sem espaço
      .filter(e => e);    // remove entradas vazias, se tiver

    const confirmSend = window.confirm(
      `Are you sure you want to send the photos to the customer? \n\nEmails:\n${emails.join('\n')}`
    );

    if (!confirmSend) return;
    //alert(url + `/extra-services/sendPhotosToCustomer/${service.id}`);

    axios.post(url + `/api/send-to-customer/${service.id}`)
      .then((x) => {
        console.info('Response: ' + JSON.stringify(x.data));
        
        // 👉 Atualiza o estado local para evidence_status = "sent"
        setProjects(prevProjects =>
          prevProjects.map(proj =>
            proj.id === service.id
            ? { ...proj, evidence_status: "sent" }
            : proj
          )
        );
        alert('Photos sent to customer successfully!'); 
      })
      .catch(error => {
        console.error('Error sending photos to customer:', error);
        alert('Error sending photos to customer. Please try again.');
      });
  };

  const sendWhatsappMessage = async (service) => {
    // DEV
    // const token = 'EAAXUZCZBrp4SEBOyyfluC8rksZCASsJAXWasWuDDQyfhZCD3ud4pLUfdjnhZAn31G8wSQKJUc7DxZAVMOzkZAZC4ZBhlMaHUgqZC6Jk9PavZAv93XAGxfFLwrE4h13yVtoFZCOY1KaKPiJusVi4tCwCUb9AcPAAWBeQJo1yDrGagEn6IQJ6MYbFPOYRKEk0dAcZCOCa42jIJskvmsTti2DPrwj7vZB7vny';

    //willias
    const token = 'EAAXUZCZBrp4SEBOxSQtIQE7PYxb9MZCS9OUWzQnwKT9dsEZC6ksXn077yfUFd66uSIH2t6YBZAcuuOHsjRauRdruvbn6QBRKBrE1qjjdFlpzsjUwR5SgON1l5Lm1oEh6ZChPlM2un3UfPMMYbZA1qiPoaYvf8yewtZAQkbs7cPmQYU4CXDQavIX32R2HZACSWyNhiYCKLFaInwe7DseJUGXDhQf6Pkv2sb3kZCKHaWNUnm';

    //temp
    //const token = 'EAAXUZCZBrp4SEBO6of0BZBYZBSDZAb6bNPBGe1QkkWLdjdtztGGIzK8VWT49RZCZBUuDb2pbDUN0USabqA4PwNkcXYkH5iyC4SnjRfUfxhzWhWnN1MAgqOLZB9TbOI8M0bKQreokMfp1gNS8vrUZC486ZCDVrBmI1zZBSA6pAtkFnTO8Q2ltQlvkzxHo6XxpMVrdyWurKyZCosQxb0shYdNTeTyFLFXC';
    const phoneNumberId = '692113570644650';
    const url = `https://graph.facebook.com/v22.0/${phoneNumberId}/messages`;
    const worker_name = users.find((u) => u.id === parseInt(service.assigned_employee)).name;
    const lines = [
      `Hello, *${worker_name}*! 👋`,
      ``,
      `New service request received!`,
      ``,
      `*Location:* ${service.service_address}`,
      `*Date:* ${service.request_date}`,
      `*Request:* ${service.service_description}`,
      ``,
      ``,
      `*Details:*`,
      `${frontendUrl}/extraService-details?id=${service.id}`
    ];

    const body = {
      messaging_product: 'whatsapp',
      to: '14084693124',
      type: 'text',
      text: {
        preview_url: true,
        body: lines.join('\n')
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      console.log('Resposta:', data);
      alert("Message sent successfully!");
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">Services</h2>
      <h2 className="text-2xl font-bold mb-4">
        Worker: {user?.name}
      </h2>

      {/* Filtro de Billing Status */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filters:
        </label>
        <div className="flex flex-wrap gap-4">
          {/* Checkbox para "All Status" */}
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              className="form-checkbox h-4 w-4 text-blue-600"
              // Está "checado" se a contagem de filtros for igual ao total de status
              checked={billingFilter.length === ALL_BILLING_STATUSES.length}
              onChange={() => handleBillingFilterChange('all')}
            />
            <span className="ml-2 text-sm text-gray-700 font-bold">All Status</span>
          </label>

          {/* Checkboxes individuais para cada status */}
          {ALL_BILLING_STATUSES.map((status) => (
            <label key={status} className="inline-flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-4 w-4 text-blue-600"
                checked={billingFilter.includes(status)}
                onChange={() => handleBillingFilterChange(status)}
              />
              <span className="ml-2 text-sm text-gray-700">
                {status.replace(/_/g, ' ').charAt(0).toUpperCase() + status.replace(/_/g, ' ').slice(1)}
              </span>
            </label>
          ))}
        </div>
      </div>

          {/* Date range filter */}
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm text-gray-700">From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded px-2 py-1"
            />
            <label className="text-sm text-gray-700">To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded px-2 py-1"
            />
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="ml-2 text-sm text-gray-600 hover:underline"
              title="Clear date range"
            >
              Clear
            </button>
          </div>

      {user?.id === 0 && (
        <div className="mb-4">
          <button
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
            onClick={() => navigate('/extraService-new')}
          >
            + New Service
          </button>
        </div>
      )}

      <div className="mb-4">
        <button
          className="bg-gray-700 hover:bg-gray-900 text-white font-semibold py-2 px-4 rounded"
          onClick={handlePrintResults}
        >
          Print results
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {filteredProjects.map((project) => (
          <div key={project.id} className="p-4 bg-white shadow rounded-2xl card flex flex-col h-full w-full">
            <div className="flex-1">
              <h1 className="mb-4">
              <span
                className="inline-block py-2 font-medium"
              >
                {project.request_date} {project.service_price ? '($' + project.service_price + ')' : ''}
              </span>
              <hr />
            </h1>

            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold
                ${project.request_status === 'pending' ? 'bg-red-200 text-red-800' : ''}
                ${project.request_status === 'completed' ? 'bg-green-200 text-green-800' : ''}
                ${project.request_status === 'forwarded' ? 'bg-blue-200 text-blue-800' : ''}
                ${project.request_status === 'ignored' ? 'bg-gray-300 text-gray-800' : ''}
              `}>
                <span className="font-semibold block text-blue-800">Request:</span>
                {project.request_status.replace('_', ' ').toUpperCase()}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ml-2 max-w-[140px] whitespace-nowrap text-center
                ${project.billing_status === 'waiting' ? 'bg-orange-200 text-orange-800' : ''}
                ${project.billing_status === 'to_invoice' ? 'bg-red-200 text-red-800' : ''}
                ${project.billing_status === 'invoiced' ? 'bg-green-200 text-green-800' : ''}
                ${project.billing_status === 'no_invoice_needed' ? 'bg-gray-300 text-gray-800' : ''}
              `}>
                <span className="font-semibold block text-blue-800">Billing: </span>
                {project.billing_status.replace(/_/g, ' ').toUpperCase()}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ml-2
                ${project.evidence_status === 'send' ? 'bg-red-200 text-red-800' : ''}
                ${project.evidence_status === 'sent' ? 'bg-green-200 text-green-800' : ''}
                ${project.evidence_status === 'ignored' ? 'bg-gray-300 text-gray-800' : ''}
                ${project.evidence_status === 'pending' ? 'bg-red-200 text-red-800' : ''}
              `}>
                <span className="font-semibold block text-blue-800">Evidence:</span>
                {project.evidence_status.replace(/_/g, ' ').toUpperCase()}
              </span>

            </div>

            <div className="items-start">
              <div>
                <p className="text-lg "><span className="text-blue-600">{project.supervisor_name}</span></p>
                <p className="text-lg mb-4">{project.service_address}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {user?.id === 0 && (
                <label className="flex flex-col items-center justify-center w-20 h-20 border border-dashed border-gray-400 rounded-lg cursor-pointer text-center text-xs text-gray-500">
                  +<br />Add Photo
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotoUpload(e, project.id)} />
                </label>
              )}
              {JSON.parse(project.reference_photos || '[]').map((src, index) => (
                <div key={index} className="relative w-20 h-20">
                  
          <img src={src}
            onClick={() => setSelectedImage({ projectId: project.id, index })}
                    referrerPolicy="no-referrer"
                    alt={`Uploaded ${index + 1}`} className="w-full h-full object-cover rounded-lg" />

                  {user?.id === 0 && (
                    <button
                      className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-5 h-5 text-xs text-black shadow"
                      onClick={() => handleRemovePhoto(index, project.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>

            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => handleCopyToClipboard(project)}
                className="bg-gray-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-700 hidden"
                title="Copiar para WhatsApp"
              >
                📋
              </button>
              {project.request_status === "completed" && (
              <button
                onClick={() => handleSendToCustomer(project)}
                className="bg-yellow-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-yellow-700"
                title="Send photos to customer"
              >
                <svg xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    className="w-6 h-6 text-white">
                  <path d="M2 21l21-9L2 3v7l15 2-15 2z"/>
                </svg>
              </button>
              )}  
              <button
                onClick={() => handleProjectClick(project.id)}
                className="bg-blue-500 text-white rounded-full w-12 h-12 items-center justify-center flex hover:bg-blue-700"
              >
                i
              </button>
              {user?.id === 0 && (
                <button
                  onClick={() => handleEditService(project.id)}
                  className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-green-700"
                  title="Edit"
                >
                  ✏️
                </button>
              )}
              {user?.id === 0 && (
                <button
                  onClick={() => handleDeleteService(project.id)}
                  className="bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700"
                  title="Delete"
                >
                  ×
                </button>
              )}
            </div>

          </div>
        ))}
      </div>

      {selectedImage && (
        <ImageModal
          images={modalImages}
          selectedIndex={modalIndex}
          onClose={() => setSelectedImage(null)}
          onChange={(newIndex) => setSelectedImage({ projectId: selectedImage.projectId, index: newIndex })}
        />
      )}

      {isUploading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
            <p className="text-white mt-4 text-lg font-semibold">Uploading...</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExtraServiceList;
